import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import Loading from 'components/Loading';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as actions from '../../redux/actions';

import TimeStampCheckOutCard from '../../components/Card/TimeStampCheckOutCard';
import CalculateSalaryperday from '../../utils/functions/CalculateSalaryperday';
import CalculateSalaryOtperday from '../../utils/functions/CalculateSalaryOtperday';
import TimeStampCheckOutType2Card from '../../components/Card/TimeStampCheckOutType2Card';
import CheckoutGpsBlockDialog from '../../components/Dialogs/CheckoutGpsBlockDialog';
import useCheckoutGpsGuard from '../../hooks/useCheckoutGpsGuard';
import { showAlert } from '../../utils/functions/globalDialog';

export default function CheckOut({ title, subtitle }) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const timestamp = useSelector((state) => state.timestamp);
  const me = useSelector((state) => state.me);
  const project = useSelector((state) => state.project);
  const holiday = useSelector((state) => state.holiday);
  const info = useSelector((state) => state.info);

  const [imgSrc, setImgSrc] = useState(null);
  const [noteCheckin, setNoteCheckin] = useState();
  const [projectSelect, setProjectSelect] = useState(timestamp?.project_in);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    coords,
    positionError,
    needsGpsCheck,
    checkGpsForCheckout,
    gpsBlockOpen,
    setGpsBlockOpen,
    gpsBlockReason,
  } = useCheckoutGpsGuard(timestamp, me, info);

  const extra = CalculateSalaryperday(holiday);
  const extra_ot = CalculateSalaryOtperday(holiday);
  const requirePhoto = me?.userData?.require_checkin_photo !== false;

  // เช็ค GPS ทันทีตอนเปิดหน้านี้ (เผื่อกรณีเข้าหน้านี้ตรง ๆ โดยไม่ผ่านปุ่มเช็คที่ ListTimestamp.js
  // มาก่อน) — รอจนกว่าข้อมูล timestamp โหลดเสร็จและผลตำแหน่ง GPS สรุปแล้ว (ได้พิกัดหรือ error)
  // ค่อยเช็คครั้งเดียว ไม่ต้องรอให้ถ่ายรูปก่อน
  const autoGpsCheckedRef = useRef(false);
  useEffect(() => {
    if (autoGpsCheckedRef.current) return;
    if (!needsGpsCheck) return;
    if (!timestamp?.isCompleted) return;
    const locationSettled = coords != null || positionError != null;
    if (!locationSettled) return;
    autoGpsCheckedRef.current = true;
    checkGpsForCheckout();
  }, [needsGpsCheck, coords, positionError, timestamp?.isCompleted]); // eslint-disable-line

  useEffect(() => {
    dispatch(actions.timestampGet(id));
    dispatch(actions.meGet({}));
    dispatch(actions.projectAll({ lite: true }));
    dispatch(actions.getInformation());
    dispatch(actions.holidayAll({ date: dayjs().startOf('date') }));
    setProjectSelect(project?.rows?.project_in);
    return () => {};
  }, []);

  const doCheckout = async () => {
    setSubmitting(true);
    const data = {
      employee: me?.userData?._id,
      status_checkOut: true,
      project_out: timestamp?.project_in,
      image_out: imgSrc,
      salary: me?.userData?.salary,
      employee_firstname: me?.userData?.firstname,
      employee_lastname: me?.userData?.lastname,
      employee_title: me?.userData?.title,
      employee_firstname_th: me?.userData?.firstname_th,
      employee_lastname_th: me?.userData?.lastname_th,
      checkIn: timestamp?.checkIn,
    };
    try {
      await dispatch(actions.timestampPut(id, data));
      history.goBack();
    } catch (error) {
      const msg = error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
      showAlert(msg, { status: error?.response?.status });
    } finally {
      setSubmitting(false);
      setWarnDialogOpen(false);
    }
  };

  const onSubmit = () => {
    if (!checkGpsForCheckout()) return;
    const hasCheckInImage = timestamp?.image?.url;
    if (!hasCheckInImage) {
      // แสดง Dialog เตือน แทน window.confirm ที่ถูก block บน mobile
      setWarnDialogOpen(true);
      return;
    }
    doCheckout();
  };

  const renderCard = () => (
    <div>
      {info?.setting?.timestamp_image ? (
        <TimeStampCheckOutCard
          me={me}
          date={new Date()}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          noteCheckin={noteCheckin}
          setNoteCheckin={setNoteCheckin}
          project={project}
          projectSelect={projectSelect}
          setProjectSelect={setProjectSelect}
          onSubmit={onSubmit}
          timestamp={timestamp}
          requirePhoto={requirePhoto}
        />
      ) : (
        <TimeStampCheckOutType2Card
          me={me}
          date={new Date()}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          noteCheckin={noteCheckin}
          setNoteCheckin={setNoteCheckin}
          project={project}
          projectSelect={projectSelect}
          setProjectSelect={setProjectSelect}
          onSubmit={onSubmit}
        />
      )}

      {/* Dialog เตือนเมื่อไม่มีรูปเช็คอิน */}
      <Dialog open={warnDialogOpen} onClose={() => setWarnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography fontWeight={700}>ไม่พบรูปภาพเช็คอิน</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            หากตรวจพบการทุจริต การเข้างานครั้งนี้จะถูกยกเลิกทันที
          </Alert>
          <Typography variant="body2" color="text.secondary">
            คุณต้องการลงเวลาออกโดยไม่มีรูปภาพเช็คอินหรือไม่?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnDialogOpen(false)} disabled={submitting}>
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={doCheckout}
            disabled={submitting}
          >
            {submitting ? 'กำลังบันทึก...' : 'ยืนยัน ลงเวลาออก'}
          </Button>
        </DialogActions>
      </Dialog>

      <CheckoutGpsBlockDialog
        open={gpsBlockOpen}
        reason={gpsBlockReason}
        onClose={() => setGpsBlockOpen(false)}
      />
    </div>
  );

  if (!timestamp?.isLoading && timestamp?.isCompleted) {
    return <div>{renderCard()}</div>;
  }
  return <Loading isLoading />;
}
